// src/components/dashboard/SalesReport.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, DollarSign, ShoppingBag, Package, 
  Calendar, Filter, Download, BarChart3, PieChart, 
  TrendingDown, ArrowUpRight, ArrowDownRight,
  Activity, Target, LineChart, BarChart2, Award
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function SalesReport({ isMobile }) {
  const [reportData, setReportData] = useState({
    period: 'week',
    startDate: new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [salesData, setSalesData] = useState(null);
  const [individualSales, setIndividualSales] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chartType, setChartType] = useState('bar');

  useEffect(() => {
    generateReport();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({ ...prev, [name]: value }));
  };

  const generateReport = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "getSalesReport",
        period: reportData.period,
        startDate: reportData.startDate,
        endDate: reportData.endDate
      });

      if (response.data.success) {
        setSalesData(response.data.summary);
        setIndividualSales(response.data.individualSales || []);
      } else {
        // Mock data for demo
        const mockData = {
          totalSales: 1240.50,
          totalOrders: 18,
          averageOrder: 68.92,
          dailyAverage: 177.21,
          growthRate: 8.5,
          topProducts: [
            { name: 'Pork Legs', sales: 450.25, quantity: 28, percentage: 36 },
            { name: 'Pork Shoulders', sales: 320.75, quantity: 25, percentage: 26 },
            { name: 'Pork Belly', sales: 280.50, quantity: 18, percentage: 23 },
            { name: 'Pork Ribs', sales: 189.00, quantity: 12, percentage: 15 }
          ],
          salesByCategory: [
            { category: 'Premium Cuts', sales: 680.25, percentage: 55 },
            { category: 'BBQ Specials', sales: 320.75, percentage: 26 },
            { category: 'Traditional Cuts', sales: 239.50, percentage: 19 }
          ],
          dailySales: [
            { day: 'Mon', sales: 150, orders: 5 },
            { day: 'Tue', sales: 220, orders: 7 },
            { day: 'Wed', sales: 180, orders: 6 },
            { day: 'Thu', sales: 250, orders: 8 },
            { day: 'Fri', sales: 320, orders: 10 },
            { day: 'Sat', sales: 280, orders: 9 },
            { day: 'Sun', sales: 190, orders: 6 }
          ]
        };
        
        setSalesData(mockData);
        
        const mockIndividualSales = Array.from({ length: 25 }, (_, i) => ({
          id: i + 1,
          orderId: `ORD${1000 + i}`,
          customerName: ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Wilson'][i % 4],
          product: ['Pork Legs', 'Pork Shoulders', 'Pork Belly', 'Pork Ribs'][i % 4],
          quantity: 1 + (i % 5),
          price: [15.99, 12.99, 9.99, 18.99][i % 4],
          total: (1 + (i % 5)) * [15.99, 12.99, 9.99, 18.99][i % 4],
          date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
          status: ['completed', 'completed', 'completed', 'processing'][i % 4]
        }));
        
        setIndividualSales(mockIndividualSales);
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
      // Fallback to mock data
      const mockData = {
        totalSales: 1240.50,
        totalOrders: 18,
        averageOrder: 68.92,
        dailyAverage: 177.21,
        growthRate: 8.5,
        topProducts: [
          { name: 'Pork Legs', sales: 450.25, quantity: 28, percentage: 36 },
          { name: 'Pork Shoulders', sales: 320.75, quantity: 25, percentage: 26 },
          { name: 'Pork Belly', sales: 280.50, quantity: 18, percentage: 23 }
        ],
        salesByCategory: [
          { category: 'Premium Cuts', sales: 680.25, percentage: 55 },
          { category: 'BBQ Specials', sales: 320.75, percentage: 26 },
          { category: 'Traditional Cuts', sales: 239.50, percentage: 19 }
        ],
        dailySales: [
          { day: 'Mon', sales: 150, orders: 5 },
          { day: 'Tue', sales: 220, orders: 7 },
          { day: 'Wed', sales: 180, orders: 6 }
        ]
      };
      
      setSalesData(mockData);
      
      const mockIndividualSales = [
        { id: 1, orderId: 'ORD001', customerName: 'John Doe', product: 'Pork Legs', quantity: 2, price: 15.99, total: 31.98, date: '2024-01-15', status: 'completed' },
        { id: 2, orderId: 'ORD002', customerName: 'Jane Smith', product: 'Pork Shoulders', quantity: 1, price: 12.99, total: 12.99, date: '2024-01-15', status: 'completed' }
      ];
      
      setIndividualSales(mockIndividualSales);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (exportType) => {
    if (!salesData) return;

    try {
      const exportData = individualSales.map((sale, index) => ({
        'SN': index + 1,
        'Order ID': sale.orderId,
        'Customer': sale.customerName,
        'Product': sale.product,
        'Quantity': sale.quantity,
        'Price': `$${sale.price}`,
        'Total': `$${sale.total}`,
        'Date': sale.date,
        'Status': sale.status
      }));

      if (exportType === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales");
        XLSX.writeFile(wb, `sales_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('Sales Report - Individual Sales', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        doc.text(`Period: ${reportData.period}`, 14, 28);
        doc.text(`Total Sales: $${salesData.totalSales}`, 14, 34);
        doc.text(`Total Orders: ${salesData.totalOrders}`, 14, 40);
        
        const tableData = exportData.map(item => [
          item.SN,
          item['Order ID'],
          item.Customer,
          item.Product,
          item.Quantity,
          item.Price,
          item.Total,
          item.Date,
          item.Status
        ]);

        doc.autoTable({
          startY: 45,
          head: [['SN', 'Order ID', 'Customer', 'Product', 'Quantity', 'Price', 'Total', 'Date', 'Status']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`sales_report_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Sales report exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export sales report.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-400';
      case 'processing': return 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400';
      case 'pending': return 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400';
      case 'cancelled': return 'bg-gradient-to-r from-red-500/20 to-pink-500/20 text-red-400';
      default: return 'bg-gray-800 text-gray-400';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            Sales Analytics
          </h2>
          <p className="text-gray-400">Track sales performance and insights</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-800 p-1 rounded-lg">
            <button
              onClick={() => setChartType('bar')}
              className={`p-2 rounded ${chartType === 'bar' ? 'bg-gray-700' : ''}`}
            >
              <BarChart3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setChartType('pie')}
              className={`p-2 rounded ${chartType === 'pie' ? 'bg-gray-700' : ''}`}
            >
              <PieChart className="w-5 h-5" />
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded ${chartType === 'line' ? 'bg-gray-700' : ''}`}
            >
              <LineChart className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex space-x-2">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Excel</span>
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleExport('pdf')}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Report Controls */}
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-3xl blur-xl"></div>
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-amber-500/20 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Report Period</label>
              <select
                name="period"
                value={reportData.period}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {reportData.period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={reportData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={reportData.endDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </>
            )}
          </div>

          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={generateReport}
            disabled={isLoading}
            className="w-full px-6 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Activity className="w-5 h-5" />
                <span>Generate Analytics Report</span>
              </>
            )}
          </motion.button>
        </div>
      </div>

      {salesData && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-2xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div className={`flex items-center space-x-1 ${salesData.growthRate >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {salesData.growthRate >= 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                    <span className="text-sm font-bold">{Math.abs(salesData.growthRate)}%</span>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white mb-2">${salesData.totalSales.toFixed(2)}</div>
                <div className="text-sm text-gray-400">Total Revenue</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600">
                    <ShoppingBag className="w-6 h-6 text-white" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{salesData.totalOrders}</div>
                <div className="text-sm text-gray-400">Total Orders</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">${salesData.averageOrder.toFixed(2)}</div>
                <div className="text-sm text-gray-400">Average Order Value</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-2xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">${salesData.dailyAverage?.toFixed(2) || '0.00'}</div>
                <div className="text-sm text-gray-400">Daily Average</div>
              </div>
            </motion.div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Products */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-400" />
                    Top Performing Products
                  </h3>
                  <span className="text-sm text-emerald-400">+8.5% growth</span>
                </div>
                <div className="space-y-4">
                  {salesData.topProducts.map((product, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-full text-sm font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-bold text-white">{product.name}</div>
                            <div className="text-sm text-gray-400">{product.quantity} units sold</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-white">${product.sales.toFixed(2)}</div>
                          <div className="text-sm text-gray-400">{product.percentage}%</div>
                        </div>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${product.percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                          className="h-2 rounded-full bg-gradient-to-r from-emerald-500 to-green-600"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sales by Category */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-purple-400" />
                  Sales Distribution by Category
                </h3>
                <div className="space-y-4">
                  {salesData.salesByCategory.map((category, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-white">{category.category}</div>
                        <div className="text-right">
                          <div className="font-bold text-white">${category.sales.toFixed(2)}</div>
                          <div className="text-sm text-gray-400">{category.percentage}%</div>
                        </div>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${category.percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                          className={`h-2 rounded-full ${index === 0 ? 'bg-gradient-to-r from-purple-500 to-pink-600' : index === 1 ? 'bg-gradient-to-r from-blue-500 to-cyan-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Daily Sales Chart */}
          {salesData.dailySales && (
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-blue-400" />
                  Weekly Sales Trend
                </h3>
                <div className="flex items-end h-48 space-x-2 md:space-x-4">
                  {salesData.dailySales.map((day, index) => {
                    const maxSales = Math.max(...salesData.dailySales.map(d => d.sales));
                    const height = (day.sales / maxSales) * 100;
                    
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div className="text-xs text-gray-400 mb-2">{day.day}</div>
                        <div className="relative w-full h-32">
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: `${height}%` }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                            className="w-full bg-gradient-to-t from-blue-500 to-cyan-600 rounded-t-lg"
                          />
                        </div>
                        <div className="text-sm text-white mt-2">${day.sales}</div>
                        <div className="text-xs text-gray-400">{day.orders} orders</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Individual Sales Table */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-gray-800/20 to-gray-900/20 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden">
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-xl font-bold text-white">Individual Sales Transactions</h3>
                <p className="text-gray-400">Detailed breakdown of all sales transactions</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Order ID</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Product</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Qty</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Price</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Total</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {individualSales.slice(0, 10).map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-800/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-bold text-blue-400">{sale.orderId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{sale.customerName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{sale.product}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{sale.quantity}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">${sale.price}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-bold text-emerald-400">${sale.total}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-gray-400">{sale.date}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(sale.status)}`}>
                            {sale.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SalesReport;